import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Copy, Check } from "lucide-react";
import { Sux1Logo } from "@/components/Sux1Logo";
import { PublicStats } from "@/components/PublicStats";
import showcase from "@/data/showcase.json";
import { trackEvent } from "@/lib/analytics";

/** Human-friendly labels for the badges shown on each card. */
const MODEL_LABEL = {
  "claude-sonnet-4-5": "Claude Sonnet 4.5",
  "gpt-5.2": "GPT-5.2",
  "gemini-3-flash": "Gemini 3 Flash",
};
const FORMAT_LABEL = {
  paragraph: "Paragraphe",
  bullets: "Points clés",
  both: "Paragraphe + points",
  philo: "Philo + citations",
  metaphore: "Métaphores (résumé imagé)",
  poeme: "Poème et récitation",
  solve: "Explication & résolution",
  dissertation: "Dissertation",
};
const LENGTH_LABEL = { short: "Court", medium: "Moyen", long: "Long" };

const Badge = ({ children, tone = "default" }) => (
  <span
    className={`inline-block font-mono uppercase tracking-[0.15em] text-[9px] sm:text-[10px] px-2 py-0.5 border ${
      tone === "accent"
        ? "border-[#002FA7] text-[#002FA7]"
        : tone === "warm"
        ? "border-[#FF2A00] text-[#FF2A00]"
        : "border-[#002FA7]/25 text-muted-foreground"
    }`}
  >
    {children}
  </span>
);

/** One showcase card. Deliberately compact by default; expand-on-click to
 *  keep the page scannable while still giving proof of quality. */
const ShowcaseCard = ({ item, index }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(item.summary);
      setCopied(true);
      trackEvent("showcase_copy", { slug: item.slug });
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };
  return (
    <article
      data-testid={`showcase-card-${item.slug}`}
      className="border border-border bg-card hover:border-[#002FA7]/40 transition-colors"
    >
      <header className="border-b border-border p-5 sm:p-6 space-y-3">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground">
            {item.kind}
          </span>
        </div>
        <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl leading-[1.1] tracking-tight">
          {item.title}
        </h2>
        <p className="font-mono text-xs text-muted-foreground">
          {item.author} · {item.original_chars.toLocaleString("fr-FR")} caractères de texte source
        </p>
        <div className="flex flex-wrap gap-1.5 pt-2">
          <Badge tone="accent">{MODEL_LABEL[item.model] || item.model}</Badge>
          <Badge>{FORMAT_LABEL[item.format] || item.format}</Badge>
          <Badge>{LENGTH_LABEL[item.length] || item.length}</Badge>
          <Badge tone="warm">
            {item.summary_chars.toLocaleString("fr-FR")} chars générés
          </Badge>
        </div>
      </header>
      <div className="p-5 sm:p-6 lg:p-8">
        <div className="prose prose-sm sm:prose-base max-w-none dark:prose-invert font-['IBM_Plex_Sans'] leading-relaxed whitespace-pre-wrap">
          {item.summary}
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            onClick={onCopy}
            data-testid={`showcase-copy-${item.slug}`}
            className="font-mono uppercase tracking-[0.15em] text-[10px] px-3 py-1.5 border border-border hover:bg-[#FF2A00] hover:text-white transition-colors flex items-center gap-2"
          >
            {copied ? (
              <>
                <Check className="w-3 h-3" /> Copié
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" /> Copier
              </>
            )}
          </button>
          <Link
            to="/signup"
            onClick={() => trackEvent("showcase_cta_click", { slug: item.slug })}
            data-testid={`showcase-cta-${item.slug}`}
            className="font-mono uppercase tracking-[0.15em] text-[10px] px-3 py-1.5 bg-[#002FA7] text-white hover:bg-[#00227A] transition-colors flex items-center gap-2"
          >
            Résumez le vôtre <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default function Showcase() {
  return (
    <main data-testid="showcase-page" className="min-h-screen bg-background text-foreground">
      {/* Top nav */}
      <nav className="border-b border-border px-4 sm:px-6 lg:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="inline-flex" data-testid="showcase-logo-link">
          <Sux1Logo size="md" />
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/demo"
            data-testid="showcase-demo-link"
            className="hidden sm:inline font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          >
            Démo animée
          </Link>
          <Link
            to="/signup"
            data-testid="showcase-signup-link"
            className="font-mono uppercase tracking-[0.18em] text-[10px] bg-[#002FA7] text-white px-4 py-2 hover:bg-[#FF2A00]/85 transition-colors"
          >
            Créer un compte
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <header className="px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-8 max-w-5xl">
        <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#002FA7] mb-4">
          Vitrine · Résumés générés par SUX1
        </p>
        <h1 className="font-['Space_Grotesk'] text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-6">
          Voyez ce que SUX1 fait,
          <br />
          <span className="text-[#002FA7]">sans même vous inscrire.</span>
        </h1>
        <p className="max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
          Quatre œuvres emblématiques passées au filtre SUX1. Textes sources réels,
          résumés bruts, aucun trucage. Chaque carte vous montre le modèle utilisé
          (Claude, GPT ou Gemini), le format et la longueur — pour que vous choisissiez
          les vôtres en connaissance de cause.
        </p>
        <div className="mt-6">
          <PublicStats variant="hero" />
        </div>
      </header>

      {/* Showcase grid */}
      <section className="px-4 sm:px-6 lg:px-10 pb-16 sm:pb-24 max-w-5xl">
        <div className="grid gap-6 sm:gap-8" data-testid="showcase-grid">
          {showcase.map((item, i) => (
            <ShowcaseCard key={item.slug} item={item} index={i} />
          ))}
        </div>
      </section>

      {/* Final CTA band */}
      <section className="border-t border-border bg-secondary/40">
        <div className="px-4 sm:px-6 lg:px-10 py-14 sm:py-20 max-w-5xl">
          <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-muted-foreground mb-4">
            À votre tour
          </p>
          <h2 className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-6">
            Testez avec votre propre texte.<br />
            <span className="text-muted-foreground">Exemples gratuits, puis dès 2 € les 20 résumés.</span>
          </h2>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/signup"
              data-testid="showcase-final-signup"
              className="inline-flex items-center gap-3 bg-[#002FA7] text-white font-mono uppercase tracking-[0.2em] text-xs px-6 py-4 hover:bg-[#00227A] transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Créer un compte gratuit
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/demo"
              data-testid="showcase-final-demo"
              className="inline-flex items-center gap-3 border border-border text-foreground font-mono uppercase tracking-[0.2em] text-xs px-6 py-4 hover:bg-[#FF2A00] hover:text-white transition-colors"
            >
              Voir la démo animée
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
